import { useNavigate } from "react-router-dom";
import PageHeading from "../../Components/PageHeading/PageHeading";
import moneyExchangeIcon from "../../Assets/Images/transaction-data.png";
import sampleIcon from "../../Assets/Images/info.png";
import { UserAuthContext } from "../../Contexts/UserAuthContext";
import { useContext, useEffect, useRef, useState } from "react";
import { Button } from "@mui/material";
import axios from "axios";
import swal from "sweetalert";
import ContentNewsPoster from "../Gallery/ContentNewsPoster";
import SamplePdfModal from "../../Components/SamplePdfModal/SamplePdfModal";
import AiFactsDisplay from "../../Components/AiFacts/AiFactsDisplay";

const ITEMS_PER_PAGE = 20;
const BASE_CREDIT = 75;

export default function TypographicNewsPoster() {
    const [allNews, setAllNews] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [isFetchingNews, setIsFetchingNews] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    const [selectedNewsId, setSelectedNewsId] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [jobStatusText, setJobStatusText] = useState("");
    const [resultData, setResultData] = useState(null);
    const [showPdfModal, setShowPdfModal] = useState(false);
    const [isCopied, setIsCopied] = useState(false);

    const newsListRef = useRef(null);


    const { AIuser, userRefetch, setUserRefetch } = useContext(UserAuthContext);
    const navigate = useNavigate();

    const credit = BASE_CREDIT;
    const creditToLocalString = AIuser?.credits
        ? parseInt(Math.floor(AIuser?.credits)).toLocaleString()
        : "0";


    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Fetch ALL news once on mount
    useEffect(() => {
        const fetchNews = async () => {
            setIsFetchingNews(true);
            try {
                const res = await fetch(
                    "https://backend.whalesai.com/api/v1/latest-news"
                );
                const data = await res.json();
                if (data.success) {
                    setAllNews(data.data);
                    setTotalCount(data.count || data.data.length);
                }
            } catch (err) {
                console.error("Failed to fetch news:", err);
            } finally {
                setIsFetchingNews(false);
            }
        };
        fetchNews();
    }, []);

    // Filter by search query (reset to page 1 when query changes)
    const filteredNews = allNews.filter((news) => {
        const q = searchQuery.toLowerCase();
        return (
            !q ||
            news.title?.toLowerCase().includes(q) ||
            news.description?.toLowerCase().includes(q)
        );
    });

    // Client-side pagination
    const totalPages = Math.ceil(filteredNews.length / ITEMS_PER_PAGE);
    const pagedNews = filteredNews.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;

        setCurrentPage(page);
        setResultData(null);

        if (newsListRef.current) {
            newsListRef.current.scrollTo({
                top: 0,
                behavior: "smooth",
            });
        }
    };


    // const handleSubmitCredits = async () => {
    //     const deductedCredits = AIuser.credits - credit;
    //     await axios
    //         .put(
    //             "https://backend.whalesai.com/api/dashboard/user_signup-signin/update-credits",
    //             { email: AIuser?.email, credits: deductedCredits },
    //             {
    //                 headers: {
    //                     authorization: `Bearer ${localStorage.getItem("aiUser")}`,
    //                 },
    //             }
    //         )
    //         .then((res) => {
    //             if (res.status === 200) {
    //                 setUserRefetch(!userRefetch);
    //             }
    //         })
    //         .catch((err) => {
    //             console.log(err);
    //         });
    // };

    const handleGenerate = async () => {
        if (!selectedNewsId) {
            swal({
                text: "Please select a news article to generate a poster.",
                icon: "warning",
                buttons: "Ok",
                className: "modal_class_success",
            });
            return;
        }

        if (!AIuser || AIuser.credits < credit) {
            swal({
                text: "You don't have enough credits! Please buy credits to continue.",
                icon: "error",
                buttons: "Buy",
                className: "modal_class_success",
            }).then((value) => {
                if (value) navigate("/purchase");
            });
            return;
        }

        setIsLoading(true);
        setJobStatusText("Generating your news poster...");
        setResultData(null);

        try {
            const response = await fetch(
                "https://backend.whalesai.com/api/v1/typographic-news-poster/generate",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        authorization: `Bearer ${localStorage.getItem("aiUser")}`,
                    },
                    body: JSON.stringify({ newsId: selectedNewsId, credit }),
                }
            );

            const data = await response.json();

            if (!data.success) {
                throw new Error(data.message || "Generation failed");
            }

            // Deduct credits after successful generation
            // await handleSubmitCredits();
            setUserRefetch(!userRefetch)

            const selectedNews = allNews.find((n) => n.newsId === selectedNewsId);
            setResultData({
                imageUrl: data.data?.imageUrl,
                title: selectedNews?.title || "Generated Poster",
                caption: data.data?.caption,
            });

            const wrapper = document.createElement("div");
            wrapper.innerHTML = `<p class="text-white">Your News Poster has been generated successfully!<br/>You can view and download.</p>`;
            swal({
                content: wrapper,
                icon: "success",
                button: "Close",
                className: "modal_class_success",
            });
        } catch (error) {
            console.error("Error generating poster:", error);
            swal({
                text: error.message || "Something went wrong!",
                icon: "error",
                buttons: "Ok",
                className: "modal_class_success",
            });
        } finally {
            setIsLoading(false);
            setJobStatusText("");
        }
    };

    const handleDownload = async ({ imageUrl }) => {
        try {
            const response = await fetch(imageUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = "news-poster.jpg";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Image download failed:", error);
        }
    };

    const handleClear = () => {
        setSelectedNewsId("");
        setResultData(null);
        setJobStatusText("");
        setSearchQuery("");
    };

    const renderCaptionWithHashtags = (text) => {
        if (!text) return null;
        return text.split(/(#\w+)/g).map((part, index) => {
            if (part.startsWith('#')) {
                return <span key={index} className="text-warning">{part}</span>;
            }
            return part;
        });
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "";

        const iso = dateStr
            .replace(" +0000", "Z")
            .replace(" ", "T");

        const d = new Date(iso);

        if (isNaN(d.getTime())) return "";

        return d.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    const getPaginationRange = () => {
        const delta = 2;
        const range = [];
        const left = Math.max(2, currentPage - delta);
        const right = Math.min(totalPages - 1, currentPage + delta);
        range.push(1);
        if (left > 2) range.push("...");
        for (let i = left; i <= right; i++) range.push(i);
        if (right < totalPages - 1) range.push("...");
        if (totalPages > 1) range.push(totalPages);
        return range;
    };

    return (
        <div>
            {/* <PageHeading
                title={"NEWS POSTER AI"}
                description={
                    "Convert the latest breaking news into eye-catching posters in a single click—perfect for fast sharing and impactful visual storytelling."
                }
                onInfoClick={() => setShowPdfModal(true)}
                infoTooltip="See sample news poster"
            /> */}
            <SamplePdfModal pdfModal={showPdfModal} setPdfModal={setShowPdfModal} title="What you can do?">
                <ContentNewsPoster />
            </SamplePdfModal>
            <div className="container pb-5">
                <div
                    style={{
                        maxWidth: "872px",
                        paddingInline: "12px",
                        marginInline: "auto",
                    }}
                >
                    <div className="container py-2">
                        {/* Credits section */}
                        <div className="d-flex align-items-center justify-content-center centralize-credits-btn-sm mt-2">
                            <div>
                                {/* <div>
                                    <button className="credits-button">
                                        CREDITS : {creditToLocalString}
                                    </button>
                                    <button
                                        className="buy-credits-button"
                                        onClick={() => navigate("/purchase")}
                                    >
                                        BUY CREDITS
                                    </button>
                                    <button
                                        onClick={() => navigate("/usage")}
                                        className="money-exchange-button"
                                    >
                                        <img src={moneyExchangeIcon} alt="moneyExchangeIcon" />
                                    </button>
                                </div> */}
                                <p className="all-page-title all-page-gradient-text text-center my-4">NEWS POSTER AI</p>
                                <div className="credits-actions">

                                    <button className="credits-button">
                                        CREDITS : {creditToLocalString}
                                    </button>

                                    <button
                                        className="buy-credits-button"
                                        onClick={() => navigate("/purchase")}
                                    >
                                        BUY CREDITS
                                    </button>

                                    <button
                                        onClick={() => navigate("/usage")}
                                        className="money-exchange-button"
                                    >
                                        <img src={moneyExchangeIcon} alt="usage" />
                                    </button>

                                    <button
                                        type="button"
                                        className="money-exchange-button"
                                        onClick={() => {
                                            setShowPdfModal(true);
                                        }}

                                    >
                                        <img src={sampleIcon} alt="See sample video" />
                                    </button>

                                </div>
                                <p
                                    style={{ fontSize: "8px" }}
                                    className="text-white mt-2 creditText text-center"
                                >
                                    {credit} Credits per poster
                                </p>
                            </div>
                        </div>

                        {/* Search bar */}
                        <div className="mt-4 mb-3">
                            <label
                                className="text-white mb-2"
                                style={{ fontSize: "14px" }}
                            >
                                Search News
                            </label>
                            <div style={{ position: "relative" }}>
                                <span
                                    style={{
                                        position: "absolute",
                                        left: "14px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        color: "#a0b8cc",
                                        fontSize: "15px",
                                        pointerEvents: "none",
                                    }}
                                >
                                    🔍
                                </span>
                                <input
                                    type="text"
                                    placeholder="Search by title or description..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    disabled={isLoading || isFetchingNews}
                                    style={{
                                        width: "100%",
                                        background: "#011C34",
                                        border: "2px solid #1a4a7a",
                                        borderRadius: "12px",
                                        color: "#fff",
                                        padding: "10px 16px 10px 40px",
                                        fontSize: "14px",
                                        outline: "none",
                                    }}
                                />
                                {searchQuery && (
                                    <button
                                        onClick={() => setSearchQuery("")}
                                        style={{
                                            position: "absolute",
                                            right: "12px",
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            background: "none",
                                            border: "none",
                                            color: "#a0b8cc",
                                            cursor: "pointer",
                                            fontSize: "16px",
                                            lineHeight: 1,
                                            padding: 0,
                                        }}
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* News list label */}
                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <label className="text-white" style={{ fontSize: "14px" }}>
                                Select a News
                            </label>
                            {selectedNewsId && (
                                <span
                                    style={{
                                        fontSize: "12px",
                                        color: "#ffa21f",
                                        cursor: "pointer",
                                    }}
                                    onClick={() => setSelectedNewsId("")}
                                >
                                    ✕ Deselect
                                </span>
                            )}
                        </div>

                        {/* News cards list */}
                        {isFetchingNews ? (
                            <div className="d-flex align-items-center justify-content-center gap-2 py-5">
                                <div
                                    className="spinner-border text-light"
                                    role="status"
                                    style={{ width: "2rem", height: "2rem" }}
                                />
                                <span className="text-white" style={{ fontSize: "14px" }}>
                                    Loading news...
                                </span>
                            </div>
                        ) : filteredNews.length === 0 ? (
                            <div
                                className="text-center py-4"
                                style={{ color: "#a0b8cc", fontSize: "14px" }}
                            >
                                No news found matching your search.
                            </div>
                        ) : (
                            <div
                                ref={newsListRef}
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "10px",
                                    maxHeight: "480px",
                                    overflowY: "auto",
                                    paddingRight: "4px",
                                }}
                            >
                                {pagedNews.map((news) => {
                                    const isSelected = selectedNewsId === news.newsId;
                                    return (
                                        <div
                                            key={news.newsId}
                                            onClick={() => {
                                                if (!isLoading) {
                                                    setSelectedNewsId(
                                                        isSelected ? "" : news.newsId
                                                    );
                                                    setResultData(null);
                                                }
                                            }}
                                            style={{
                                                background: isSelected
                                                    ? "rgba(255,162,31,0.08)"
                                                    : "#011C34",
                                                border: isSelected
                                                    ? "2px solid #ffa21f"
                                                    : "2px solid #1a4a7a",
                                                borderRadius: "12px",
                                                padding: "14px 16px",
                                                cursor: isLoading ? "not-allowed" : "pointer",
                                                display: "flex",
                                                alignItems: "flex-start",
                                                gap: "14px",
                                                transition:
                                                    "border-color 0.2s, background 0.2s",
                                                opacity: isLoading ? 0.6 : 1,
                                            }}
                                        >
                                            {/* Checkbox */}
                                            <div
                                                style={{
                                                    width: "20px",
                                                    height: "20px",
                                                    minWidth: "20px",
                                                    borderRadius: "50%",
                                                    border: isSelected
                                                        ? "2px solid #ffa21f"
                                                        : "2px solid #a0b8cc",
                                                    background: isSelected
                                                        ? "#ffa21f"
                                                        : "transparent",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    marginTop: "2px",
                                                    flexShrink: 0,
                                                    transition:
                                                        "background 0.2s, border-color 0.2s",
                                                }}
                                            >
                                                {isSelected && (
                                                    <svg
                                                        width="11"
                                                        height="8"
                                                        viewBox="0 0 11 8"
                                                        fill="none"
                                                    >
                                                        <path
                                                            d="M1 4L4 7L10 1"
                                                            stroke="#011C34"
                                                            strokeWidth="2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    </svg>
                                                )}
                                            </div>

                                            {/* Content */}
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <p
                                                    style={{
                                                        color: isSelected ? "#ffa21f" : "#ffffff",
                                                        fontSize: "13px",
                                                        fontWeight: "600",
                                                        margin: "0 0 6px 0",
                                                        lineHeight: "1.5",
                                                        transition: "color 0.2s",
                                                    }}
                                                >
                                                    {news.title}
                                                </p>
                                                {news.description &&
                                                    news.description !== "None" && (
                                                        <p
                                                            style={{
                                                                color: "#a0b8cc",
                                                                fontSize: "12px",
                                                                margin: "0 0 8px 0",
                                                                lineHeight: "1.5",
                                                                display: "-webkit-box",
                                                                WebkitLineClamp: 2,
                                                                WebkitBoxOrient: "vertical",
                                                                overflow: "hidden",
                                                            }}
                                                        >
                                                            {news.description}
                                                        </p>
                                                    )}
                                                <div className="d-flex flex-wrap gap-2">
                                                    {news.author && (
                                                        <span
                                                            style={{
                                                                fontSize: "10px",
                                                                color: "#ffa21f",
                                                                background:
                                                                    "rgba(255,162,31,0.1)",
                                                                padding: "2px 7px",
                                                                borderRadius: "5px",
                                                            }}
                                                        >
                                                            {news.author}
                                                        </span>
                                                    )}
                                                    {news.published && (
                                                        <span
                                                            style={{
                                                                fontSize: "10px",
                                                                color: "#a0b8cc",
                                                                background:
                                                                    "rgba(0,0,0,0.25)",
                                                                padding: "2px 7px",
                                                                borderRadius: "5px",
                                                            }}
                                                        >
                                                            {formatDate(news.published)}
                                                        </span>
                                                    )}
                                                    {news.category?.slice(0, 2).map((cat) => (
                                                        <span
                                                            key={cat}
                                                            style={{
                                                                fontSize: "10px",
                                                                color: "#6ec5ff",
                                                                background:
                                                                    "rgba(110,197,255,0.1)",
                                                                padding: "2px 7px",
                                                                borderRadius: "5px",
                                                                textTransform: "capitalize",
                                                            }}
                                                        >
                                                            {cat}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Pagination */}
                        {!isFetchingNews && totalPages > 1 && (
                            <div className="d-flex justify-content-center align-items-center flex-wrap gap-1 mt-4 mb-2">
                                <button
                                    className="btn btn-sm"
                                    style={{
                                        background:
                                            currentPage === 1 ? "#0b2d4d" : "#1a4a7a",
                                        color: "#fff",
                                        border: "1px solid #1a4a7a",
                                        borderRadius: "8px",
                                        padding: "4px 12px",
                                        fontSize: "13px",
                                    }}
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1 || isLoading}
                                >
                                    ‹
                                </button>
                                {getPaginationRange().map((item, idx) =>
                                    item === "..." ? (
                                        <span
                                            key={`ellipsis-${idx}`}
                                            className="text-white"
                                            style={{ padding: "4px 6px", fontSize: "13px" }}
                                        >
                                            ...
                                        </span>
                                    ) : (
                                        <button
                                            key={item}
                                            className="btn btn-sm"
                                            style={{
                                                background:
                                                    currentPage === item
                                                        ? "linear-gradient(90deg, #ffa21f, #ff6b00)"
                                                        : "#0b2d4d",
                                                color: "#fff",
                                                border:
                                                    currentPage === item
                                                        ? "none"
                                                        : "1px solid #1a4a7a",
                                                borderRadius: "8px",
                                                padding: "4px 12px",
                                                fontSize: "13px",
                                                fontWeight:
                                                    currentPage === item ? "bold" : "normal",
                                            }}
                                            onClick={() => handlePageChange(item)}
                                            disabled={isLoading}
                                        >
                                            {item}
                                        </button>
                                    )
                                )}
                                <button
                                    className="btn btn-sm"
                                    style={{
                                        background:
                                            currentPage === totalPages
                                                ? "#0b2d4d"
                                                : "#1a4a7a",
                                        color: "#fff",
                                        border: "1px solid #1a4a7a",
                                        borderRadius: "8px",
                                        padding: "4px 12px",
                                        fontSize: "13px",
                                    }}
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages || isLoading}
                                >
                                    ›
                                </button>
                                <span
                                    className="text-white ms-2"
                                    style={{ fontSize: "12px", opacity: 0.7 }}
                                >
                                    Page {currentPage} of {totalPages} ({totalCount} articles)
                                </span>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="d-flex justify-content-center align-items-center mb-5 mt-4">
                            <Button
                                sx={{
                                    padding: "8px 30px",
                                    marginRight: "10px",
                                    color: "#000",
                                    background: "#fff",
                                    "&:hover": { background: "#fff" },
                                    "&:disabled": { background: "#fff" },
                                }}
                                onClick={handleClear}
                                disabled={isLoading}
                                variant="contained"
                            >
                                Clear
                            </Button>
                            <Button
                                sx={{
                                    padding: "8px 30px",
                                    "&:disabled": { background: "gray" },
                                }}
                                onClick={handleGenerate}
                                className="generate-button text-white"
                                disabled={isLoading || !selectedNewsId}
                                variant="contained"
                            >
                                {isLoading ? "Processing..." : "Generate"}
                            </Button>
                        </div>

                        <div className="animate text-md-center text-lg-center text-center mb-5 d-flex justify-content-center force-small-font">
                            Please do not navigate away from the browser.
                            <br /> Doing so will stop the process.
                        </div>
                        <div className="text-md-center text-white text-lg-center text-center d-flex justify-content-center force-small-font mt-1">
                            While AI strives for accuracy, it may occasionally make mistakes. <br />Please verify any important information.
                        </div>

                        {/* Loading section */}
                        {isLoading && (
                            <div className="d-flex flex-column align-items-center mt-4 mb-3">
                                <div
                                    className="spinner-border text-light mb-3"
                                    role="status"
                                    style={{ width: "3rem", height: "3rem" }}
                                >
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                {jobStatusText && (
                                    <p
                                        className="text-warning text-center mb-2"
                                        style={{ fontSize: "14px" }}
                                    >
                                        {jobStatusText}
                                    </p>
                                )}
                            </div>
                        )}

                        {isLoading && <AiFactsDisplay />}

                        {/* Generated Poster */}
                        {resultData && !isLoading && (
                            <div className="mt-5 d-flex flex-column align-items-center justify-content-center">
                                {resultData.title && (
                                    <p className="text-white fs-5 fw-bold text-center mb-3">
                                        {resultData.title}
                                    </p>
                                )}
                                {resultData.imageUrl && (
                                    <>
                                        <p
                                            className="text-warning mt-2 mb-2"
                                            style={{ fontSize: "13px" }}
                                        >
                                            Generated Poster
                                        </p>
                                        <img
                                            src={resultData.imageUrl}
                                            alt="Generated News Poster"
                                            className="img-fluid"
                                            style={{
                                                borderRadius: "12px",
                                                maxWidth: "600px",
                                                width: "100%",
                                                border: "2px solid #1a4a7a",
                                            }}
                                        />
                                        <button
                                            className="btn btn-success mt-3"
                                            onClick={() =>
                                                handleDownload({
                                                    imageUrl: resultData.imageUrl,
                                                })
                                            }
                                        >
                                            <i className="fa-solid fa-download" /> Download
                                        </button>

                                        {resultData.caption && (
                                            <div className="mt-4 p-3 d-flex flex-column align-items-center" style={{ background: "rgba(255, 255, 255, 0.1)", borderRadius: "8px", maxWidth: "600px", width: "100%" }}>
                                                <p className="text-white mb-2 text-center" style={{ fontSize: "14px", fontStyle: "italic", whiteSpace: "pre-wrap" }}>
                                                    {renderCaptionWithHashtags(resultData.caption)}
                                                </p>
                                                <button
                                                    className="btn btn-sm btn-outline-light mt-2"
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(resultData.caption);
                                                        setIsCopied(true);
                                                        setTimeout(() => setIsCopied(false), 2000);
                                                    }}
                                                >
                                                    <i className={isCopied ? "fa-solid fa-check text-success" : "fa-regular fa-copy"}></i> {isCopied ? "Copied" : "Copy"}
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}